import { Injectable, inject } from '@angular/core';
import { NarratorService } from './narrator-service';
import {
  CYOA_CONTINUATION_LOADING_VISIT,
  IMAGE_STYLES,
  ImageStyleName,
  NARRATIVE_STYLE_CHOOSE_YOUR_OWN,
  NARRATIVE_STYLE_STANDARD,
  type NarrativeStyleName,
} from '@storees/shared-constants';
import { SessionService } from './session.service';
import { StoryElementInputKey, StoryElementKey } from '@storees/api-types';
import type { components } from '@storees/api-types';
import { NarratorEntitlement } from '@storees/api-types';
import { NarratorVoice } from '@storees/api-types';
import { MinimalProfile } from './profile-service';
import { StoryData } from '../utils/StoryUtils';
import { ProfileGender } from '@storees/api-types';
import { calculateAge } from '../utils/date-utils';
import { VoiceCloneService } from './voice-clone.service';
import { TrophiesService } from './trophies-service';
import { MilestoneTrophyType } from './trophy-types';
import { selectWeightedGenre as selectWeightedGenreShared } from '@storees/daily-story';
import { logger } from '../utils/logger';

type Story = components['schemas']['Story'];
type StoryElement = components['schemas']['StoryElementInput'];

interface StoryParams {
  name?: string;
  description?: string;
  narratorId?: string;
}

interface StoryElementsByKey {
  genre: StoryParams;
  setting: StoryParams;
  theme: StoryParams;
  conflict: StoryParams;
  character: StoryParams;
  plot: StoryParams;
  tone: StoryParams;
  wordOfTheDay: StoryParams;
  length: StoryParams;
  /** choose_your_own | standard (name) */
  narrativeStyle: StoryParams;
  /** segment count 3–10 as string in name */
  cyoaSegmentCount: StoryParams;
  imageStyle: StoryParams;
  narratorVoice: StoryParams;
}

export interface PendingNarratorFallbackNotice {
  originalNarratorId?: string;
  originalNarratorName?: string;
  fallbackNarratorId?: string;
  fallbackNarratorName: string;
}

@Injectable({
  providedIn: 'root',
})
export class StoryCreationStateService {
  private readonly sessionService = inject(SessionService);

  private _storyElements: StoryElementsByKey = {
    genre: {},
    setting: {},
    theme: {},
    conflict: {},
    character: {},
    plot: {},
    tone: {},
    wordOfTheDay: {},
    length: {},
    narrativeStyle: {},
    cyoaSegmentCount: {},
    imageStyle: {},
    narratorVoice: {},
  };

  private _continuationPreviousStoryId: string | undefined;
  private _continuationSelectedChoiceIndex: number | undefined;

  // New character-specific properties
  private _mainCharacterAdvancedDescription = '';
  private _mainCharacterName = '';
  private _selectedRole = '';
  private _selectedType = '';
  private _selectedAvatars: boolean[] = [];
  private _adjustComplexity = true;
  private _storyMainProfileId = '';
  private pendingNarratorFallbackNotice: PendingNarratorFallbackNotice | null =
    null;

  // Character-specific methods
  setMainCharacterAdvancedDescription(description: string): void {
    this._mainCharacterAdvancedDescription = description;
  }

  getMainCharacterAdvancedDescription(): string {
    return this._mainCharacterAdvancedDescription;
  }

  setMainCharacterName(name: string): void {
    this._mainCharacterName = name;
  }

  getMainCharacterName(): string {
    return this._mainCharacterName;
  }

  setSelectedRole(role: string): void {
    this._selectedRole = role;
  }

  getSelectedRole(): string {
    return this._selectedRole;
  }

  setSelectedType(type: string): void {
    this._selectedType = type;
  }

  getSelectedType(): string {
    return this._selectedType;
  }

  setSelectedAvatars(avatars: boolean[]): void {
    this._selectedAvatars = [...avatars];
  }

  getSelectedAvatars(): boolean[] {
    return [...this._selectedAvatars];
  }

  setAdjustComplexity(adjust: boolean): void {
    this._adjustComplexity = adjust;
  }

  getAdjustComplexity(): boolean {
    return this._adjustComplexity;
  }

  setStoryMainProfileId(profileId: string): void {
    this._storyMainProfileId = profileId;
  }

  getStoryMainProfileId(): string {
    return this._storyMainProfileId;
  }

  resetAllParams(): void {
    this._storyElements = {
      genre: {},
      setting: {},
      theme: {},
      conflict: {},
      character: {},
      plot: {},
      tone: {},
      wordOfTheDay: {},
      length: {},
      narrativeStyle: {},
      cyoaSegmentCount: {},
      imageStyle: {},
      narratorVoice: {},
    };

    // Reset character-specific properties
    this._mainCharacterAdvancedDescription = '';
    this._mainCharacterName = '';
    this._selectedRole = '';
    this._selectedType = '';
    this._selectedAvatars = [];
    this._adjustComplexity = true;
    this._storyMainProfileId = '';
    this.pendingNarratorFallbackNotice = null;
  }

  consumePendingNarratorFallbackNotice():
    | PendingNarratorFallbackNotice
    | null {
    const notice = this.pendingNarratorFallbackNotice;
    this.pendingNarratorFallbackNotice = null;
    return notice;
  }

  /**
   * Load story creation state from a story's elements
   * Maps StoryElementKey enums (from Story response) to the corresponding state fields
   */
  loadFromStoryElements(story: Story): void {
    if (!story.elements || story.elements.length === 0) {
      return;
    }

    // Reset current state
    this.resetAllParams();

    // Map each element to the corresponding state field
    story.elements.forEach(element => {
      const params: StoryParams = {
        name: element.name || undefined,
        description: element.description || undefined
      };

      switch (element.key) {
        case StoryElementKey.Genre:
          this._storyElements.genre = params;
          break;
        case StoryElementKey.Setting:
          this._storyElements.setting = params;
          break;
        case StoryElementKey.Theme:
          this._storyElements.theme = params;
          break;
        case StoryElementKey.Conflict:
          this._storyElements.conflict = params;
          break;
        case StoryElementKey.Plot:
          this._storyElements.plot = params;
          break;
        case StoryElementKey.Character:
          this._storyElements.character = params;
          break;
        case StoryElementKey.Tone:
          this._storyElements.tone = params;
          break;
        case StoryElementKey.WordOfTheDay:
          this._storyElements.wordOfTheDay = params;
          break;
        case StoryElementKey.Length:
          this._storyElements.length = params;
          break;
        case StoryElementKey.NarrativeStyle:
          this._storyElements.narrativeStyle = params;
          break;
        case StoryElementKey.CyoaSegmentCount:
          this._storyElements.cyoaSegmentCount = params;
          break;
        case StoryElementKey.ImageStyle:
          this._storyElements.imageStyle = params;
          break;
        case StoryElementKey.NarratorVoice:
          this._storyElements.narratorVoice = params;
          break;
        // Note: StoryLanguage is commented out in your service, so skipping it
        default:
          console.warn(`Unknown story element key: ${element.key}`);
      }
    });
  }

  /**
   * Load state from StoryRequest-style elements (e.g., daily push payload).
   */
  loadFromRequestElements(elements: StoryElement[]): void {
    if (!elements || elements.length === 0) {
      return;
    }

    this.resetAllParams();

    elements.forEach((element) => {
      const params: StoryParams = {
        name: element.name || undefined,
        description: element.description || undefined,
      };

      switch (element.key) {
        case StoryElementInputKey.Genre:
          this._storyElements.genre = params;
          break;
        case StoryElementInputKey.Setting:
          this._storyElements.setting = params;
          break;
        case StoryElementInputKey.Theme:
          this._storyElements.theme = params;
          break;
        case StoryElementInputKey.Conflict:
          this._storyElements.conflict = params;
          break;
        case StoryElementInputKey.Plot:
          this._storyElements.plot = params;
          break;
        case StoryElementInputKey.Character:
          this._storyElements.character = params;
          break;
        case StoryElementInputKey.Tone:
          this._storyElements.tone = params;
          break;
        case StoryElementInputKey.WordOfTheDay:
          this._storyElements.wordOfTheDay = params;
          break;
        case StoryElementInputKey.Length:
          this._storyElements.length = params;
          break;
        case StoryElementInputKey.NarrativeStyle:
          this._storyElements.narrativeStyle = params;
          break;
        case StoryElementInputKey.CyoaSegmentCount:
          this._storyElements.cyoaSegmentCount = params;
          break;
        case StoryElementInputKey.ImageStyle:
          this._storyElements.imageStyle = params;
          break;
        case StoryElementInputKey.NarratorVoice:
          this._storyElements.narratorVoice = params;
          break;
        default:
          break;
      }
    });
  }

  /**
   * Load state from a story found in recent or favorite stories
   * Uses StoryUtils to find the story object and then loads its elements
   */
  loadFromStoryData(storyData: StoryData, profile: MinimalProfile): void {
    // You can use StoryUtils to find the story object here if needed
    const storyObject = this.findStoryObjectByData(storyData, profile);

    if (storyObject) {
      this.loadFromStoryElements(storyObject);
    } else {
      console.warn('Could not find story object to load elements from');
    }
  }

  /**
   * Helper method to find story object from StoryData
   */
  private findStoryObjectByData(storyData: StoryData, profile: MinimalProfile): Story | undefined {
    if (!profile?.recentStories && !profile?.favoriteStories) {
      return undefined;
    }

    // Check recent stories first
    let story = profile.recentStories?.find((s: Story) =>
      s.audioUrl.includes(storyData.audioFilename)
    );

    // If not found in recent, check favorites
    if (!story) {
      story = profile.favoriteStories?.find((s: Story) =>
        s.audioUrl.includes(storyData.audioFilename)
      );
    }

    return story;
  }

  /**
   * Check if the current state has any parameters set
   */
  hasAnyParameters(): boolean {
    return Object.values(this._storyElements).some((param) =>
      Boolean(param && (param.name || param.description))
    );
  }

  setGenreParams(name?: string, description?: string): void {
    this._storyElements.genre = {
      name,
      description,
    };
  }

  getGenreParams(): StoryParams {
    return { ...this._storyElements.genre };
  }

  setSettingParams(name?: string, description?: string): void {
    this._storyElements.setting = {
      name,
      description,
    };
  }

  getSettingParams(): StoryParams {
    return { ...this._storyElements.setting };
  }

  setThemeParams(name?: string, description?: string): void {
    this._storyElements.theme = {
      name,
      description,
    };
  }

  getThemeParams(): StoryParams {
    return { ...this._storyElements.theme };
  }

  setCharacterParams(name?: string, description?: string): void {
    this._storyElements.character = {
      name,
      description,
    };
  }

  getCharacterParams(): StoryParams {
    return { ...this._storyElements.character };
  }

  setConflictParams(name?: string, description?: string): void {
    this._storyElements.conflict = {
      name,
      description,
    };
  }

  getConflictParams(): StoryParams {
    return { ...this._storyElements.conflict };
  }

  setPlotParams(name?: string, description?: string): void {
    this._storyElements.plot = {
      name,
      description,
    };
  }

  getPlotParams(): StoryParams {
    return { ...this._storyElements.plot };
  }

  setToneParams(name?: string, description?: string): void {
    this._storyElements.tone = {
      name,
      description,
    };
  }

  getToneParams(): StoryParams {
    return { ...this._storyElements.tone };
  }

  setWordOfTheDayParams(name?: string, description?: string): void {
    this._storyElements.wordOfTheDay = {
      name,
      description,
    };
  }

  getWordOfTheDayParams(): StoryParams {
    return { ...this._storyElements.wordOfTheDay };
  }

  setStoryLengthParams(name?: string, description?: string): void {
    this._storyElements.length = {
      name,
      description,
    };
  }

  getLengthParams(): StoryParams {
    return { ...this._storyElements.length };
  }

  setNarrativeStyleParams(name?: string, description?: string): void {
    this._storyElements.narrativeStyle = { name, description };
  }

  getNarrativeStyleParams(): StoryParams {
    return { ...this._storyElements.narrativeStyle };
  }

  setCyoaSegmentCountParams(name?: string, description?: string): void {
    this._storyElements.cyoaSegmentCount = { name, description };
  }

  getCyoaSegmentCountParams(): StoryParams {
    return { ...this._storyElements.cyoaSegmentCount };
  }

  isChooseYourOwnNarrative(): boolean {
    const id = (this._storyElements.narrativeStyle.name ?? '').toLowerCase();
    return id === 'choose_your_own';
  }

  setContinuationParams(previousStoryId: string, selectedChoiceIndex: number): void {
    if (!previousStoryId) {
      logger.error(
        'StoryCreationStateService.setContinuationParams called with empty previousStoryId — request will fail backend validation',
        { previousStoryId, selectedChoiceIndex },
      );
    }
    if (
      typeof selectedChoiceIndex !== 'number' ||
      Number.isNaN(selectedChoiceIndex) ||
      selectedChoiceIndex < 0
    ) {
      logger.error(
        'StoryCreationStateService.setContinuationParams called with invalid selectedChoiceIndex',
        { previousStoryId, selectedChoiceIndex },
      );
    }
    logger.info('StoryCreationStateService: setContinuationParams', {
      previousStoryId,
      selectedChoiceIndex,
    });
    this.setNarrativeStyleParams(
      'choose_your_own',
      'Choose your own adventure',
    );
    this._continuationPreviousStoryId = previousStoryId;
    this._continuationSelectedChoiceIndex = selectedChoiceIndex;
    void this.sessionService.setPersistent(CYOA_CONTINUATION_LOADING_VISIT, 'true');
  }

  getContinuationPayload(): {
    previousStoryId?: string;
    selectedChoiceIndex?: number;
  } {
    if (
      this._continuationPreviousStoryId !== undefined &&
      this._continuationSelectedChoiceIndex !== undefined
    ) {
      return {
        previousStoryId: this._continuationPreviousStoryId,
        selectedChoiceIndex: this._continuationSelectedChoiceIndex,
      };
    }
    return {};
  }

  clearContinuationParams(): void {
    if (
      this._continuationPreviousStoryId !== undefined ||
      this._continuationSelectedChoiceIndex !== undefined
    ) {
      logger.info('StoryCreationStateService: clearContinuationParams', {
        clearedPreviousStoryId: this._continuationPreviousStoryId,
        clearedSelectedChoiceIndex: this._continuationSelectedChoiceIndex,
      });
    }
    this._continuationPreviousStoryId = undefined;
    this._continuationSelectedChoiceIndex = undefined;
    void this.sessionService.setPersistent(CYOA_CONTINUATION_LOADING_VISIT, '');
  }

  setImageStyleParams(name?: string, description?: string): void {
    this._storyElements.imageStyle = {
      name,
      description,
    };
  }

  getImageStyleParams(): StoryParams {
    return { ...this._storyElements.imageStyle };
  }

  // TODO add back if we use storyLanguage
  // setStoryLanguageParams(name?: string, description?: string): void {
  //   this._storyLanguage = {
  //     name,
  //     description,
  //   };
  // }
  //
  // getLanguageParams(): StoryParams {
  //   return { ...this._storyLanguage };
  // }

  setNarratorVoiceParams(
    name?: string,
    description?: string,
    narratorId?: string
  ): void {
    const narratorVoice: StoryParams = {
      name,
      description,
    };

    if (narratorId) {
      narratorVoice.narratorId = narratorId;
    }

    this._storyElements.narratorVoice = narratorVoice;
  }

  getNarratorVoiceParams(): StoryParams {
    return { ...this._storyElements.narratorVoice };
  }

  /**
   * Build the API-ready elements array for StoryRequest
   */
  buildElements(): StoryElement[] {
    const out: StoryElement[] = [];

    const tryPush = (key: StoryElementInputKey, p?: StoryParams) => {
      if (!p) return;
      const hasData = Boolean(p.name || p.description);
      if (!hasData) return;
      out.push({
        key,
        name: p.name,
        description: p.description,
      });
    };

    tryPush(StoryElementInputKey.Genre, this._storyElements.genre);
    tryPush(StoryElementInputKey.Setting, this._storyElements.setting);
    tryPush(StoryElementInputKey.Theme, this._storyElements.theme);
    tryPush(StoryElementInputKey.Conflict, this._storyElements.conflict);
    tryPush(StoryElementInputKey.Plot, this._storyElements.plot);
    tryPush(StoryElementInputKey.Character, this._storyElements.character);
    tryPush(StoryElementInputKey.Tone, this._storyElements.tone);
    if (!this.isChooseYourOwnNarrative()) {
      tryPush(StoryElementInputKey.Length, this._storyElements.length);
    }
    // Image style and narrator are top-level on StoryRequest — not duplicated in elements.

    return out;
  }

  /**
   * Kept for UI preview/debug purposes; not used by the backend anymore.
   */
  generateFinalPrompt(): string {
    return Object.entries(this._storyElements)
      .filter(([key, param]) =>
        key !== 'imageStyle' &&
        key !== 'narratorVoice' &&
        Boolean(param && ((param as StoryParams).name || (param as StoryParams).description))
      )
      .map(([key, p]) => {
        const param = p as StoryParams;
        const parts: string[] = [];
        if (param.name) parts.push(param.name);
        if (param.description) parts.push(`description: ${param.description}`);
        return `${key}: ${parts.join(', ')}`;
      })
      .join('\n\n');
  }

  // Helper method to select weighted genre based on gender
  private selectWeightedGenre(gender: ProfileGender): { name: string; description: string } {
    return selectWeightedGenreShared(gender);
  }

  // Helper method to get age-appropriate gender string
  private getGenderString(gender: ProfileGender, age: number): string {
    const isMale = gender === ProfileGender.Male;

    if (age < 4) return isMale ? 'little boy' : 'little girl';
    if (age < 10) return isMale ? 'boy' : 'girl';
    if (age < 13) return isMale ? 'young boy' : 'young girl';
    if (age < 16) return isMale ? 'teenage boy' : 'teenage girl';
    if (age < 20) return isMale ? 'young man' : 'young woman';
    return isMale ? 'man' : 'woman';
  }

  // Helper method to generate character description from profile
  private generateCharacterFromProfile(
    profile: MinimalProfile,
    options?: { randomTrait?: boolean }
  ): string {
    const age = calculateAge(profile.birthdate);
    const genderString = profile.gender ? this.getGenderString(profile.gender, age) : 'child';

    // Build character description from profile attributes
    const parts: string[] = [`A ${age}-year-old ${genderString} named ${profile.name}`];

    // Add physical characteristics if available
    if (profile.hairColor) {
      parts.push(`with ${this.formatHairColor(profile.hairColor)} hair`);
    }

    if (profile.eyeColor) {
      parts.push(`and ${this.formatEyeColor(profile.eyeColor)} eyes`);
    }

    const traits = [
      'brave and adventurous',
      'curious and clever',
      'kind and compassionate',
      'determined and resourceful',
      'creative and imaginative',
    ];
    const useRandom = options?.randomTrait !== false;
    const trait = useRandom
      ? traits[Math.floor(Math.random() * traits.length)] ?? traits[0]
      : traits[0];
    parts.push(`who is ${trait}`);

    return parts.join(' ');
  }

  /** Sets only the main character from the profile (no genre/setting randomization). */
  setCharacterFromProfile(profile: MinimalProfile): void {
    const description = this.generateCharacterFromProfile(profile, {
      randomTrait: false,
    });
    this.setCharacterParams('characters', description);
  }

  /**
   * Sets narrator voice to the profile's default narrator (profile.narratorId), or the fallback narrator.
   */
  applyNarratorFromProfileDefault(
    narratorService: NarratorService,
    profile: MinimalProfile | undefined
  ): void {
    this.setNarratorFromProfileDefault(narratorService, profile);
  }

  // Helper method to format hair color enum values
  private formatHairColor(hairColor: string): string {
    // Convert enum values like "DarkBrown" to "dark brown"
    return hairColor.replace(/([A-Z])/g, ' $1').trim().toLowerCase();
  }

  // Helper method to format eye color enum values
  private formatEyeColor(eyeColor: string): string {
    // Convert enum values like "LightBlue" to "light blue"
    return eyeColor.replace(/([A-Z])/g, ' $1').trim().toLowerCase();
  }

  // Randomly generate all story parameters
  async generateRandomStory(
    narratorService: NarratorService,
    currentProfile?: MinimalProfile,
    voiceCloneService?: VoiceCloneService,
    trophiesService?: TrophiesService,
    options?: { surpriseMe?: boolean; narrativeStyle?: NarrativeStyleName }
  ) {
    this.resetAllParams(); // Reset all parameters first

    // Random Genre selection based on profile gender
    let randomGenre: { name: string; description: string };

    if (currentProfile?.gender) {
      randomGenre = this.selectWeightedGenre(currentProfile.gender);
    } else {
      // Fallback to equal probability if no profile
      const genres = [
        {
          name: 'Fantasy',
          description:
            'A story set in a magical world with supernatural elements, mythical creatures, and an epic quest where imagination knows no bounds.',
        },
        {
          name: 'Action/Adventure',
          description:
            'A fast-paced narrative filled with daring feats, physical challenges, and a courageous hero overcoming dangerous obstacles.',
        },
        {
          name: 'Sci-Fi',
          description:
            'A futuristic tale exploring advanced technology, space exploration, time travel, and the impact of scientific discovery on society.',
        },
        {
          name: 'Romance',
          description:
            'A story centered around a relationship, emotional connection, and the journey of finding and nurturing love despite challenges.',
        },
        {
          name: 'Spooky',
          description:
            'A spooky tale designed to evoke trepidation through a supernatural entity, psychological alarm, or an unsettling situation.',
        },
        {
          name: 'Fairy Tale',
          description:
            'An enchanting story often involving a magical being, wonder, and a moral lesson, typically with a happy ending for a virtuous character.',
        },
        {
          name: 'Mystery',
          description:
            'An intriguing story involving a puzzle, secret, and investigation where a character works to uncover the truth behind an unusual event.',
        },
        {
          name: 'Comedy',
          description:
            'A humorous story designed to entertain and evoke laughter through witty dialogue, an amusing situation, and playful characters.',
        },
      ];
      randomGenre = genres[Math.floor(Math.random() * genres.length)];
    }

    this.setGenreParams(randomGenre.name, randomGenre.description);

    // Random Setting selection
    const settings = [
      {
        name: 'Enchanted Forest',
        description:
          'A magical woodland filled with mystical creatures, ancient trees, and hidden wonders where unexpected enchantments await discovery.',
      },
      {
        name: 'Desert',
        description:
          'A vast, arid landscape of sand dunes and extreme temperatures where survival depends on resourcefulness and determination.',
      },
      {
        name: 'Major City',
        description:
          'A bustling urban center with towering skyscrapers, diverse neighborhoods, and the constant energy of countless lives intersecting.',
      },
      {
        name: 'Outer Space',
        description:
          'The vast cosmos beyond Earth where stars, planets, and galaxies exist in the infinite vacuum, offering endless exploration.',
      },
      {
        name: 'Mountains',
        description:
          'A majestic range of towering peaks with challenging terrain, breathtaking vistas, and the raw power of nature on display.',
      },
      {
        name: 'Oceans and Seas',
        description:
          'A vast underwater world with mysterious depths, colorful marine life, and the powerful forces of tide and current.',
      },
      {
        name: 'Fantasy Realm',
        description:
          'An imaginary world with its own rules of magic, mythical creatures, and extraordinary landscapes beyond normal reality.',
      },
    ];
    const randomSetting = settings[Math.floor(Math.random() * settings.length)];
    this.setSettingParams(randomSetting.name, randomSetting.description);

    // Random Theme selection
    const themes = [
      {
        name: 'Good versus Evil',
        description:
          'A struggle between moral forces of right and wrong, highlighting virtuous actions against malevolent ones.',
      },
      {
        name: 'Friendship',
        description:
          'A bond between individuals characterized by mutual affection, trust, and support through various circumstances.',
      },
      {
        name: 'Courage',
        description:
          'The strength to face fear, danger, or difficulty despite feeling afraid, showcasing inner strength.',
      },
      {
        name: 'Imagination',
        description:
          'The creative ability to form new ideas, images, or concepts that transcend ordinary experience.',
      },
      {
        name: 'Teamwork',
        description:
          'The collaborative effort of a group to achieve a common goal through cooperation and mutual support.',
      },
      {
        name: 'Family',
        description:
          "A group of related individuals who share bonds of love, loyalty, and mutual support through life's journey.",
      },
    ];
    const randomTheme = themes[Math.floor(Math.random() * themes.length)];
    this.setThemeParams(randomTheme.name, randomTheme.description);

    // Random Conflict selection
    const conflicts = [
      {
        name: 'Hero vs Villain',
        description:
          'A classic struggle between a protagonist and an antagonist, where the hero must overcome a villain who represents opposing values or goals.',
      },
      {
        name: 'Moral Dilemma',
        description:
          'A situation where a character must choose between two difficult options with no clear right answer, testing their values and principles.',
      },
      {
        name: 'Identity Crisis',
        description:
          "A character's struggle to understand who they truly are, often triggered by life changes that cause them to question their purpose and sense of self.",
      },
      {
        name: 'Rebellion',
        description:
          'A resistance against an established authority, system, or rule where characters fight for change against a controlling power.',
      },
      {
        name: 'Nature',
        description:
          'A conflict between humans and the natural world, where characters face environmental challenges, survival situations, or the consequences of disturbing natural balance.',
      },
    ];
    const randomConflict =
      conflicts[Math.floor(Math.random() * conflicts.length)];
    this.setConflictParams(randomConflict.name, randomConflict.description);

    // Random Plot selection
    const plots = [
      {
        name: 'Treasure Hunt',
        description:
          'A journey in search of valuable or significant object that leads to adventure, discovery, and often personal growth through the challenges encountered along the way.',
      },
      {
        name: 'Overcoming Challenges',
        description:
          'A narrative focused on a character facing and overcoming significant obstacles through determination, growth, and resilience.',
      },
      {
        name: 'Quest',
        description:
          'A mission or journey undertaken by a character to achieve a specific goal, often involving trials that test their resolve and transform them along the way.',
      },
      {
        name: 'Voyage and Return',
        description:
          'An adventure where the protagonist ventures into an unfamiliar world before returning home transformed by the experience and insights gained.',
      },
    ];
    const randomPlot = plots[Math.floor(Math.random() * plots.length)];
    this.setPlotParams(randomPlot.name, randomPlot.description);

    // Random Tone selection
    const tones = [
      {
        name: 'Happy Ending',
        description:
          'A narrative that concludes with a positive resolution, bringing joy and satisfaction to the reader.',
      },
      {
        name: 'Exciting',
        description:
          'A thrilling and stimulating tone that creates a sense of adventure and anticipation.',
      },
      {
        name: 'Warm',
        description:
          'A tender and affectionate atmosphere that creates a feeling of comfort and emotional closeness.',
      },
      {
        name: 'Funny',
        description:
          'A humorous approach that aims to amuse and entertain through wit, comedy, or amusing situations.',
      },
      {
        name: 'Inspiring',
        description:
          'A motivational tone that uplifts the reader and encourages them to envision new possibilities.',
      },
    ];
    const randomTone = tones[Math.floor(Math.random() * tones.length)];
    this.setToneParams(randomTone.name, randomTone.description);

    // Character selection - use current profile if available
    let characterDescription: string;

    if (currentProfile) {
      characterDescription = this.generateCharacterFromProfile(currentProfile);
    } else {
      // Fallback to random character generation
      const characterRoles = ['Hero', 'Villain', 'Sidekick', 'Mentor'];
      const characterTypes = ['Human', 'Animal', 'Magical Creature', 'Robot'];
      const characterTraits = [
        'brave and determined',
        'curious and inventive',
        'wise and patient',
        'mischievous but kind-hearted',
        'clever and resourceful',
      ];

      const randomRole =
        characterRoles[Math.floor(Math.random() * characterRoles.length)];
      const randomType =
        characterTypes[Math.floor(Math.random() * characterTypes.length)];
      const randomTrait =
        characterTraits[Math.floor(Math.random() * characterTraits.length)];

      characterDescription = `Main Character: A ${randomRole.toLowerCase()} who is a ${randomType.toLowerCase()} that is ${randomTrait}`;
    }

    this.setCharacterParams('characters', characterDescription);

    // Default narrative style (onboarding surprise stories may override to Standard).
    const narrativeStyle =
      options?.narrativeStyle ?? NARRATIVE_STYLE_CHOOSE_YOUR_OWN;
    if (narrativeStyle === NARRATIVE_STYLE_STANDARD) {
      this.setNarrativeStyleParams('standard', 'Standard');
    } else {
      this.setNarrativeStyleParams(
        'choose_your_own',
        'Choose your own adventure',
      );
      this.setCyoaSegmentCountParams('5', '5');
    }
    this.setImageStyleParams(IMAGE_STYLES[0].id, ImageStyleName.CARTOON_3D);

    if (options?.surpriseMe) {
      if (voiceCloneService && trophiesService) {
        const picked = await this.pickSurpriseMeNarrator(
          narratorService,
          currentProfile,
          voiceCloneService,
          trophiesService
        );
        this.setNarratorVoiceParams(picked.voice, picked.name, picked.id);
      } else {
        const heart = this.getSurpriseMeAfHeartNarrator(narratorService);
        this.setNarratorVoiceParams(heart.voice, heart.name, heart.id);
      }
    } else {
      // Filter out ff_siwis narrator voice from random selection
      const narratorVoices = await this.getRandomNarratorPool(
        narratorService,
        voiceCloneService,
        trophiesService
      );
      let randomNarratorVoice;
      if (narratorVoices.length > 0) {
        randomNarratorVoice =
          narratorVoices[Math.floor(Math.random() * narratorVoices.length)];
      }
      if (randomNarratorVoice) {
        this.setNarratorVoiceParams(
          randomNarratorVoice.voice,
          randomNarratorVoice.name,
          randomNarratorVoice.id
        );
      }
    }
  }

  /** Returns true if params have a name or description set */
  private hasParamValue(p: StoryParams | undefined): boolean {
    return Boolean(p && (p.name || p.description));
  }

  /**
   * Generate a random story for "Generate Now" flow: only randomizes story elements
   * that have NOT yet been set. Preserves all existing selections in the state service.
   * Ensures the narrator voice is available (falls back to af_heart if user would hit
   * a paywall for custom/premium voices).
   */
  async generateRandomStoryForGenerateNow(
    narratorService: NarratorService,
    currentProfile: MinimalProfile | undefined,
    voiceCloneService: VoiceCloneService,
    trophiesService: TrophiesService,
    _preserveCharacter = false
  ): Promise<void> {
    // Save all existing selections before randomizing
    const saved = {
      genre: this.hasParamValue(this.getGenreParams()) ? { ...this.getGenreParams() } : undefined,
      setting: this.hasParamValue(this.getSettingParams()) ? { ...this.getSettingParams() } : undefined,
      theme: this.hasParamValue(this.getThemeParams()) ? { ...this.getThemeParams() } : undefined,
      conflict: this.hasParamValue(this.getConflictParams()) ? { ...this.getConflictParams() } : undefined,
      plot: this.hasParamValue(this.getPlotParams()) ? { ...this.getPlotParams() } : undefined,
      tone: this.hasParamValue(this.getToneParams()) ? { ...this.getToneParams() } : undefined,
      character: this.hasParamValue(this.getCharacterParams()) ? { ...this.getCharacterParams() } : undefined,
      length: this.hasParamValue(this.getLengthParams()) ? { ...this.getLengthParams() } : undefined,
      narrativeStyle: this.hasParamValue(this.getNarrativeStyleParams())
        ? { ...this.getNarrativeStyleParams() }
        : undefined,
      cyoaSegmentCount: this.hasParamValue(this.getCyoaSegmentCountParams())
        ? { ...this.getCyoaSegmentCountParams() }
        : undefined,
      imageStyle: this.hasParamValue(this.getImageStyleParams()) ? { ...this.getImageStyleParams() } : undefined,
      narratorVoice: this.hasParamValue(this.getNarratorVoiceParams()) ? { ...this.getNarratorVoiceParams() } : undefined,
      mainCharacterAdvancedDescription: this.getMainCharacterAdvancedDescription() || undefined,
      mainCharacterName: this.getMainCharacterName() || undefined,
      selectedRole: this.getSelectedRole() || undefined,
      selectedType: this.getSelectedType() || undefined,
      selectedAvatars: this.getSelectedAvatars().length > 0 ? [...this.getSelectedAvatars()] : undefined,
      adjustComplexity: this.getAdjustComplexity(),
      storyMainProfileId: this.getStoryMainProfileId() || undefined,
    };

    await this.generateRandomStory(narratorService, currentProfile);

    // Restore all saved selections
    if (saved.genre) this.setGenreParams(saved.genre.name, saved.genre.description);
    if (saved.setting) this.setSettingParams(saved.setting.name, saved.setting.description);
    if (saved.theme) this.setThemeParams(saved.theme.name, saved.theme.description);
    if (saved.conflict) this.setConflictParams(saved.conflict.name, saved.conflict.description);
    if (saved.plot) this.setPlotParams(saved.plot.name, saved.plot.description);
    if (saved.tone) this.setToneParams(saved.tone.name, saved.tone.description);
    if (saved.character) this.setCharacterParams(saved.character.name, saved.character.description);
    if (saved.length) this.setStoryLengthParams(saved.length.name, saved.length.description);
    if (saved.narrativeStyle) {
      this.setNarrativeStyleParams(saved.narrativeStyle.name, saved.narrativeStyle.description);
    }
    if (saved.cyoaSegmentCount) {
      this.setCyoaSegmentCountParams(
        saved.cyoaSegmentCount.name,
        saved.cyoaSegmentCount.description,
      );
    }
    if (saved.imageStyle) this.setImageStyleParams(saved.imageStyle.name, saved.imageStyle.description);
    // Narrator: use saved selection if they chose one on story options; otherwise use profile's default
    if (saved.narratorVoice) {
      this.setNarratorVoiceParams(
        saved.narratorVoice.name,
        saved.narratorVoice.description,
        saved.narratorVoice.narratorId
      );
    } else {
      this.setNarratorFromProfileDefault(narratorService, currentProfile);
    }
    if (saved.mainCharacterAdvancedDescription !== undefined) this.setMainCharacterAdvancedDescription(saved.mainCharacterAdvancedDescription);
    if (saved.mainCharacterName !== undefined) this.setMainCharacterName(saved.mainCharacterName);
    if (saved.selectedRole) this.setSelectedRole(saved.selectedRole);
    if (saved.selectedType) this.setSelectedType(saved.selectedType);
    if (saved.selectedAvatars) this.setSelectedAvatars(saved.selectedAvatars);
    this.setAdjustComplexity(saved.adjustComplexity);
    if (saved.storyMainProfileId) this.setStoryMainProfileId(saved.storyMainProfileId);

    await this.ensureNarratorVoiceAvailable(
      narratorService,
      voiceCloneService,
      trophiesService
    );
  }

  /**
   * Sets narrator voice from profile's default (profile.narratorId).
   * Same logic as Word of the Day: use profile narrator if available, else first narrator.
   * Uses af_heart if no narrators loaded.
   */
  private setNarratorFromProfileDefault(
    narratorService: NarratorService,
    currentProfile: MinimalProfile | undefined
  ): void {
    const fallbackNarrator = this.getFallbackNarrator(narratorService);
    const narrators = narratorService.narratorVoices;
    if (!narrators || narrators.length === 0) {
      this.setNarratorVoiceParams(
        fallbackNarrator.voice,
        fallbackNarrator.name,
        fallbackNarrator.id
      );
      return;
    }
    let narrator:
      | { id?: string; voice?: string; name?: string }
      | undefined;
    if (currentProfile?.narratorId) {
      narrator = narrators.find((n) => n.id === currentProfile.narratorId);
      if (!narrator) {
        narrator = fallbackNarrator;
      }
    } else {
      narrator = fallbackNarrator;
    }
    this.setNarratorVoiceParams(
      narrator?.voice || fallbackNarrator.voice,
      narrator?.name || fallbackNarrator.name,
      narrator?.id
    );
  }

  /**
   * If the current narrator voice would require a paywall (custom/premium voices
   * without sufficient entitlement), fall back to af_heart.
   * Uses same logic as Word of the Day: getPaywallForStoryCreation.
   *
   * When `currentProfile` is supplied and the state has no narrator selection
   * yet (e.g. a CYOA continuation kicked off straight from Story Player
   * without a Word-of-the-Day style narrator pick), the state is first
   * hydrated from the profile's default narrator. This mirrors the backend
   * resolution rule in `POST /v1/stories`: when no `narratorId` is sent on
   * the wire, the server falls back to `profile.narratorId` — so a profile
   * default that is a locked voice clone needs to trigger the same swap +
   * `PendingNarratorFallbackNotice` here, otherwise the request would 403
   * with "No active voice clone narration access" on the loading screen.
   */
  async ensureNarratorVoiceAvailable(
    narratorService: NarratorService,
    voiceCloneService: VoiceCloneService,
    trophiesService: TrophiesService,
    currentProfile?: MinimalProfile,
    options?: { allowProfileVoiceCloneRestore?: boolean }
  ): Promise<void> {
    let narratorParams = this.getNarratorVoiceParams();
    if (!narratorParams.name && !narratorParams.narratorId && currentProfile) {
      this.setNarratorFromProfileDefault(narratorService, currentProfile);
      narratorParams = this.getNarratorVoiceParams();
    }

    const voiceId = narratorParams.name;
    const selectedNarrator = narratorParams.narratorId
      ? narratorService.narratorVoices.find(
          (n) => n.id === narratorParams.narratorId
        )
      : narratorService.narratorVoices.find((n) => n.voice === voiceId);
    if (!voiceId && !selectedNarrator) return;

    const storyCount = await trophiesService.getTrophyCount(
      MilestoneTrophyType.MilestoneStoriesCreate
    );
    const paywall =
      await voiceCloneService.getFreshPaywallForStoryCreation(storyCount);

    const allowProfileVoiceCloneRestore =
      options?.allowProfileVoiceCloneRestore !== false;
    if (allowProfileVoiceCloneRestore) {
      await this.restoreProfileVoiceCloneAfterEntitlementUnlock(
        narratorService,
        currentProfile,
        paywall,
      );
    }
    narratorParams = this.getNarratorVoiceParams();

    const resolvedVoiceId = narratorParams.name;
    const resolvedNarrator = narratorParams.narratorId
      ? narratorService.narratorVoices.find(
          (n) => n.id === narratorParams.narratorId
        )
      : narratorService.narratorVoices.find((n) => n.voice === resolvedVoiceId);

    const isVoiceCloneNarrator =
      resolvedNarrator?.voice === NarratorVoice.voice_clone ||
      VoiceCloneService.isVoiceCloneNarrator(resolvedVoiceId ?? '') ||
      VoiceCloneService.isVoiceCloneNarrator(narratorParams.narratorId ?? '');
    const isFreeVoice =
      !isVoiceCloneNarrator &&
      (!resolvedNarrator ||
        resolvedNarrator.entitlement === NarratorEntitlement.Free);

    if (isFreeVoice) return;

    const shouldForceVoiceCloneFallbackForOptimisticMismatch =
      isVoiceCloneNarrator &&
      paywall === null &&
      voiceCloneService.hadOptimisticOverride?.() === true &&
      voiceCloneService.hasPaidStoreSubscriptionEntitlement?.() !== true;

    if (paywall !== null || shouldForceVoiceCloneFallbackForOptimisticMismatch) {
      const fallbackNarrator = this.getFallbackNarrator(narratorService);
      this.setNarratorVoiceParams(
        fallbackNarrator.voice,
        fallbackNarrator.name,
        fallbackNarrator.id
      );

      if (isVoiceCloneNarrator) {
        this.pendingNarratorFallbackNotice = {
          originalNarratorId:
            narratorParams.narratorId || resolvedNarrator?.id || resolvedVoiceId,
          originalNarratorName:
            narratorParams.description || resolvedNarrator?.name,
          fallbackNarratorId: fallbackNarrator.id,
          fallbackNarratorName: fallbackNarrator.name,
        };
      }
    }
  }

  /**
   * When the user previously accepted an af_heart fallback for a locked profile
   * voice clone and later gains narration access (subscription, ad-reward week,
   * or trial), restore the profile-default clone instead of keeping Heart on
   * the wire payload.
   */
  private async restoreProfileVoiceCloneAfterEntitlementUnlock(
    narratorService: NarratorService,
    currentProfile: MinimalProfile | undefined,
    paywall: Awaited<
      ReturnType<VoiceCloneService['getFreshPaywallForStoryCreation']>
    >,
  ): Promise<void> {
    if (paywall !== null || !currentProfile?.narratorId) {
      return;
    }

    const narrators = narratorService.narratorVoices ?? [];
    const profileNarrator = narrators.find(
      (n) => n.id === currentProfile.narratorId,
    );
    if (!profileNarrator) {
      return;
    }

    const profileDefaultIsVoiceClone =
      profileNarrator.voice === NarratorVoice.voice_clone ||
      VoiceCloneService.isVoiceCloneNarrator(profileNarrator.id ?? '');
    if (!profileDefaultIsVoiceClone) {
      return;
    }

    const current = this.getNarratorVoiceParams();
    const fallbackNarrator = this.getFallbackNarrator(narratorService);
    const onHeartFallback =
      current.name === fallbackNarrator.voice ||
      current.narratorId === fallbackNarrator.id;
    if (!onHeartFallback) {
      return;
    }

    const ackKey = `voice_clone_story_fallback_notice_ack_${profileNarrator.id}`;
    if (this.sessionService.get(ackKey) !== 'true') {
      return;
    }

    this.setNarratorVoiceParams(
      profileNarrator.voice,
      profileNarrator.name,
      profileNarrator.id,
    );
    this.pendingNarratorFallbackNotice = null;
    this.sessionService.set(ackKey, undefined);
  }

  /**
   * Surprise Me: af_heart only when there are no voice clones or narration is not allowed.
   * If entitled and clones exist: use profile default when it is a voice clone; otherwise
   * pick uniformly at random from available voice clones.
   */
  private async pickSurpriseMeNarrator(
    narratorService: NarratorService,
    currentProfile: MinimalProfile | undefined,
    voiceCloneService: VoiceCloneService,
    trophiesService: TrophiesService
  ): Promise<{ id?: string; voice: string; name: string }> {
    const heart = this.getSurpriseMeAfHeartNarrator(narratorService);
    const narrators = narratorService.narratorVoices ?? [];
    const voiceCloneNarrators = narrators.filter(
      (n) =>
        n.voice === NarratorVoice.voice_clone ||
        VoiceCloneService.isVoiceCloneNarrator(n.id ?? '')
    );

    const storyCount = await trophiesService.getTrophyCount(
      MilestoneTrophyType.MilestoneStoriesCreate
    );
    const paywall =
      await voiceCloneService.getPaywallForStoryCreation(storyCount);
    if (paywall !== null || voiceCloneNarrators.length === 0) {
      return heart;
    }

    const profileNarratorId = currentProfile?.narratorId?.trim();
    if (profileNarratorId) {
      const profileNarrator = narrators.find((n) => n.id === profileNarratorId);
      const profileDefaultIsVoiceClone =
        !!profileNarrator &&
        (profileNarrator.voice === NarratorVoice.voice_clone ||
          VoiceCloneService.isVoiceCloneNarrator(profileNarratorId));
      if (profileDefaultIsVoiceClone && profileNarrator) {
        return {
          id: profileNarrator.id,
          voice: profileNarrator.voice ?? NarratorVoice.voice_clone,
          name: profileNarrator.name ?? heart.name,
        };
      }
    }

    const picked =
      voiceCloneNarrators[
        Math.floor(Math.random() * voiceCloneNarrators.length)
      ] ?? voiceCloneNarrators[0];
    return {
      id: picked.id,
      voice: picked.voice ?? NarratorVoice.voice_clone,
      name: picked.name ?? heart.name,
    };
  }

  private async getRandomNarratorPool(
    narratorService: NarratorService,
    voiceCloneService?: VoiceCloneService,
    trophiesService?: TrophiesService
  ) {
    const narratorVoices = narratorService.narratorVoices ?? [];
    const baseVoices = narratorVoices.filter(
      (voice) => voice.voice !== 'ff_siwis'
    );

    if (!voiceCloneService || !trophiesService) {
      return baseVoices;
    }

    const storyCount = await trophiesService.getTrophyCount(
      MilestoneTrophyType.MilestoneStoriesCreate
    );
    const paywall = await voiceCloneService.getPaywallForStoryCreation(
      storyCount
    );
    const canUseVoiceCloneNarration = paywall === null;

    return baseVoices.filter((voice) => {
      if (voice.voice === NarratorVoice.voice_clone) {
        return canUseVoiceCloneNarration;
      }

      return voice.entitlement === NarratorEntitlement.Free;
    });
  }

  /**
   * Surprise Me fallback: strictly af_heart (never another stock narrator such as af_bella).
   * If the loaded narrator list has no Heart row, still returns voice `af_heart`.
   */
  private getSurpriseMeAfHeartNarrator(narratorService: NarratorService): {
    id?: string;
    voice: string;
    name: string;
  } {
    const narrators = narratorService.narratorVoices ?? [];
    const heartNarrator = narrators.find(
      (n) => n.voice === NarratorVoice.af_heart
    );
    if (heartNarrator) {
      return {
        id: heartNarrator.id,
        voice: heartNarrator.voice || NarratorVoice.af_heart,
        name: heartNarrator.name || 'Heart',
      };
    }
    return {
      voice: NarratorVoice.af_heart,
      name: 'Heart',
    };
  }

  private getFallbackNarrator(narratorService: NarratorService): {
    id?: string;
    voice: string;
    name: string;
  } {
    const narrators = narratorService.narratorVoices ?? [];
    const heartNarrator = narrators.find((n) => n.voice === 'af_heart');
    if (heartNarrator) {
      return {
        id: heartNarrator.id,
        voice: heartNarrator.voice || 'af_heart',
        name: heartNarrator.name || 'Heart',
      };
    }

    const firstFreeNarrator = narrators.find(
      (n) => n.entitlement === NarratorEntitlement.Free
    );
    if (firstFreeNarrator) {
      return {
        id: firstFreeNarrator.id,
        voice: firstFreeNarrator.voice || 'af_heart',
        name: firstFreeNarrator.name || 'Heart',
      };
    }

    return {
      id: narrators[0]?.id,
      voice: narrators[0]?.voice || 'af_heart',
      name: narrators[0]?.name || 'Heart',
    };
  }
}
